/*
 * Shared front-end behaviour for the Diversity showcase product grid:
 * lazy per-card Swiper galleries (arrows + dot pagination) and the row reveal
 * animation. Used by sections/diversity-showcase.liquid AND
 * sections/main-collection-product-grid.liquid, so both share one source of
 * truth. Loaded once per page; each section instance calls
 * ShowcaseGrid.init(scope) with its section id (the CSS class suffix).
 */
(function () {
  if (window.ShowcaseGrid) return;

  var states = {};

  function mountSwiper(container, scope) {
    if (container.dataset.showcaseMounted || !window.Swiper) return;
    container.dataset.showcaseMounted = 'true';
    // Dot pagination: a small, always-legible rotating window of bullets
    // (dynamicBullets) instead of a progress bar whose per-slide segment
    // width used to shrink toward invisibility on products with many images.
    // The pagination element lives as a sibling AFTER the swiper container in
    // the markup (not inside it — the swiper's own overflow: hidden, used to
    // clip the image to its rounded corners, was clipping/overlapping the
    // dots against the image's bottom edge instead of placing them below),
    // so it's found from the card, the same way the arrows are just below.
    var card = container.closest('.showcase-card-' + scope);
    var paginationEl = card ? card.querySelector('.showcase-card__pagination-' + scope) : null;
    var swiper = new window.Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 300,
      touchAngle: 45,
      watchOverflow: true,
      pagination: paginationEl
        ? {
            el: paginationEl,
            type: 'bullets',
            dynamicBullets: true,
            clickable: true,
          }
        : false,
    });
    if (!card) return;
    var prev = card.querySelector('.showcase-card__arrow--prev-' + scope);
    var next = card.querySelector('.showcase-card__arrow--next-' + scope);
    if (prev) prev.addEventListener('click', function () { swiper.slidePrev(); });
    if (next) next.addEventListener('click', function () { swiper.slideNext(); });
  }

  // Each card's gallery is mounted lazily and exactly once, when it gets near
  // the viewport (600px early, so it is interactive by the time it is seen).
  function initSwipers(scope, state) {
    var containers = document.querySelectorAll('.showcase-card__swiper-' + scope);
    if (!('IntersectionObserver' in window)) {
      containers.forEach(function (el) { mountSwiper(el, scope); });
      return;
    }
    if (!state.swiperIO) {
      state.swiperIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            mountSwiper(entry.target, scope);
            state.swiperIO.unobserve(entry.target);
          });
        },
        { rootMargin: '600px 0px' }
      );
    }
    containers.forEach(function (el) {
      if (el.dataset.showcaseMounted || el.dataset.showcaseWatched) return;
      el.dataset.showcaseWatched = 'true';
      state.swiperIO.observe(el);
    });
  }

  function initReveal(scope, state) {
    if (!document.documentElement.classList.contains('showcase-reveal-on')) return;
    var root = document.querySelector('.section-' + scope + '-padding');
    if (!root) return;
    if (!state.revealIO) {
      var desktop = window.matchMedia('(min-width: 750px)');
      state.firstBatch = true;
      // 1) Reveal: a row animates in whenever it enters the lower part of the
      //    viewport while hidden. Rows are grouped without measuring layout:
      //    cards of one grid row share the same top edge, so entries reported
      //    together are grouped by their (already computed)
      //    boundingClientRect.top. Stagger applies only among rows arriving in
      //    the same batch, never by page position.
      state.revealIO = new IntersectionObserver(
        function (entries) {
          var rows = {};
          entries.forEach(function (entry) {
            var el = entry.target;
            if (!entry.isIntersecting || el.classList.contains('is-revealed')) return;
            var kind = el.getAttribute('data-showcase-reveal');
            var inFeature = !!el.parentElement.closest('[data-showcase-reveal="feature"]');
            var passive = desktop.matches ? kind === 'fimage' || (kind === 'card' && inFeature) : kind === 'feature';
            if (passive) {
              el.classList.add('is-revealed');
              return;
            }
            var key = Math.round(entry.boundingClientRect.top);
            (rows[key] = rows[key] || []).push(el);
          });
          var keys = Object.keys(rows).map(Number).sort(function (a, b) { return a - b; });
          keys.forEach(function (key, i) {
            var delay = (state.firstBatch ? 150 : 0) + Math.min(i, 4) * 140;
            rows[key].forEach(function (el) {
              el.style.transitionDelay = delay + 'ms';
              el.classList.add('is-revealed');
            });
          });
          if (keys.length) state.firstBatch = false;
        },
        // Negative bottom margin: a row starts once ~8% of the viewport height
        // of it is on screen, so the rise is seen while scrolling.
        { rootMargin: '0px 0px -8% 0px', threshold: 0 }
      );
      // 2) Re-arm: once a revealed row is completely out of the viewport (or
      //    its tab is hidden) it silently returns to the hidden state, so it
      //    animates again the next time it scrolls into view. Only while fully
      //    off-screen with transitions suppressed, so no reverse animation is
      //    ever visible.
      state.resetIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var el = entry.target;
            if (entry.isIntersecting || !el.classList.contains('is-revealed')) return;
            el.style.transition = 'none';
            el.style.transitionDelay = '0ms';
            el.classList.remove('is-revealed');
            window.setTimeout(function () { el.style.transition = ''; }, 60);
          });
        },
        { rootMargin: '0px', threshold: 0 }
      );
    }
    root.querySelectorAll('[data-showcase-reveal]').forEach(function (el) {
      if (el.dataset.revealWatched) return;
      el.dataset.revealWatched = 'true';
      state.revealIO.observe(el);
      state.resetIO.observe(el);
    });
    if (window.__showcaseRevealPending) window.__showcaseRevealPending[scope] = false;
  }

  // Replays the reveal on cards whose visibility just changed: they drop back
  // to the hidden state with no transition and are handed to the reveal
  // observer again, so the usual staggered row animation plays.
  function rearm(scope, cards) {
    var state = states[scope];
    if (!state || !state.revealIO) return;
    cards.forEach(function (el) {
      el.style.transition = 'none';
      el.style.transitionDelay = '0ms';
      el.classList.remove('is-revealed');
      state.revealIO.unobserve(el);
    });
    window.requestAnimationFrame(function () {
      cards.forEach(function (el) {
        el.style.transition = '';
        state.revealIO.observe(el);
      });
    });
  }

  // Collection tabs: local filters over the products of the CURRENT collection
  // page. Buttons only toggle which already-rendered cards are visible; no
  // navigation, no URL change, no scrolling.
  function initTabs(scope) {
    var root = document.querySelector('.section-' + scope + '-padding');
    var bar = root && root.querySelector('[data-showcase-tabs]');
    if (!bar || bar.dataset.tabsReady) return;
    bar.dataset.tabsReady = 'true';
    var activeClass = 'showcase-tab--active-' + scope;
    bar.addEventListener('click', function (event) {
      var btn = event.target.closest('button[data-match]');
      if (!btn || btn.classList.contains(activeClass)) return;
      bar.querySelectorAll('button[data-match]').forEach(function (b) {
        var on = b === btn;
        b.classList.toggle(activeClass, on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var wanted = btn.getAttribute('data-match').split(',').map(function (m) {
        return '|' + m.trim() + '|';
      }).filter(function (m) { return m !== '||'; });
      var shown = [];
      root.querySelectorAll('[data-showcase-terms]').forEach(function (card) {
        var terms = card.getAttribute('data-showcase-terms');
        var keep = !wanted.length || wanted.some(function (m) { return terms.indexOf(m) > -1; });
        card.setAttribute('data-showcase-filtered', keep ? 'in' : 'out');
        if (keep) shown.push(card);
        else card.classList.remove('is-revealed');
      });
      rearm(scope, shown);
    });
  }

  window.ShowcaseGrid = {
    initTabs: initTabs,
    init: function (scope) {
      var state = (states[scope] = states[scope] || {});
      initSwipers(scope, state);
      initReveal(scope, state);
    },
    // For grids whose product list is replaced by AJAX (collection filters /
    // sorting / pagination): re-initialise only the new nodes after each swap.
    watch: function (containerId, scope) {
      var el = document.getElementById(containerId);
      if (!el || el.dataset.showcaseWatching) return;
      el.dataset.showcaseWatching = 'true';
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          window.ShowcaseGrid.init(scope);
        });
      }).observe(el, { childList: true, subtree: true });
    },
  };
})();
